/**
 * GlobalTranslator — MutationObserver-based DOM translation plugin.
 *
 * ─── How it works ───────────────────────────────────────────────────────────
 *
 *  1. A single MutationObserver is attached to document.body and watches
 *     EVERY text node that appears in the DOM — across every component.
 *
 *  2. Three translation tables are built from the locale files:
 *
 *       forwardMap   { English → Hindi }   exact whole-string match
 *       reverseMap   { Hindi → English }   exact whole-string match (restore)
 *       fragments    [{ en, lng }, ...]   substring match for mixed content
 *
 *  3. When a text node is seen, translateNode() tries in order:
 *       a. Whole-string exact match in the appropriate map (fast path)
 *       b. Fragment substring replacement (handles "Count: 42" etc.)
 *
 *  4. Vue data is NEVER modified. When Vue reactively updates a node's
 *     value (e.g. "Count: 42" → "Count: 50"), the characterData mutation
 *     fires and the new value is re-translated via fragments.
 *
 *  5. The `_fragments` key in a locale file marks its leaves as fragments
 *     instead of exact-match entries. Example:
 *
 *       en.json: { "_fragments": { "count": "Count: " } }
 *       hi.json: { "_fragments": { "count": "गिनती: " } }
 *
 *     Result: any text node containing "Count: " gets that prefix
 *     replaced with "गिनती: ", leaving the trailing number untouched.
 *
 * ─── Zero component changes ─────────────────────────────────────────────────
 *  Templates unchanged. Scripts unchanged. Data unchanged.
 *  To add a new module: add keys to en.json + hi.json. Nothing else.
 *
 * ─── Infinite-loop prevention ───────────────────────────────────────────────
 *  The _paused flag suppresses observer callbacks during our own DOM writes.
 */

import { loadLocale, localeCache } from './i18n'

// ── Internal state ────────────────────────────────────────────────────────────

var currentLang = 'en'

// English → translated  (exact whole-string match)
var forwardMap = {}

// Translated → English  (used when restoring back to English)
var reverseMap = {}

// Fragments: substring entries for mixed static+dynamic text
//   [{ en: "Count: ", lng: "गिनती: " }, ...]
// Sorted by en.length DESC so longer prefixes win over shorter ones.
var fragments = []

var observer = null
var _paused  = false

// Tags whose text content must never be translated
var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1 }

/**
 * Returns true if `el` is an icon element whose text content is an icon
 * ligature name (e.g. "close", "delete", "arrow_back") rather than user text.
 *
 *   <v-icon>close</v-icon>
 *     ↓ Vuetify renders ↓
 *   <i aria-hidden="true" class="v-icon material-icons">close</i>
 *
 * The Material Icons font turns the literal text "close" into the X glyph.
 * Translating it to "बंद करें" would replace the icon with literal Hindi text.
 *
 * Detected by classes — covers all Material Icons font variants
 * (outlined, round, sharp, two-tone) plus Vuetify's own `v-icon` marker.
 */
function isIconElement (el) {
  if (!el || el.nodeType !== 1 || !el.classList) return false
  if (el.classList.contains('v-icon')) return true
  for (var i = 0; i < el.classList.length; i++) {
    if (el.classList[i].indexOf('material-icons') === 0) return true
  }
  return false
}

// ── Map builder ───────────────────────────────────────────────────────────────

/**
 * Walks the en/hi trees in parallel and populates forwardMap, reverseMap,
 * and fragments. Any subtree under the key "_fragments" is treated as
 * fragment entries instead of exact-match entries.
 */
function buildMaps (lang) {
  forwardMap = {}
  reverseMap = {}
  fragments  = []

  if (lang === 'en') {
    console.log('[translator] language is English — maps cleared')
    return
  }

  var enTree   = localeCache['en'] || {}
  var langTree = localeCache[lang] || {}

  ;(function flatten (enNode, langNode, isFragment) {
    Object.keys(enNode).forEach(function (k) {
      var eVal = enNode[k]
      var lVal = langNode ? langNode[k] : undefined
      var nowFragment = isFragment || k === '_fragments'

      if (typeof eVal === 'string' && typeof lVal === 'string' && eVal !== lVal) {
        if (nowFragment) {
          fragments.push({ en: eVal, lng: lVal })
        } else {
          forwardMap[eVal] = lVal
          reverseMap[lVal] = eVal
        }
      } else if (eVal !== null && typeof eVal === 'object') {
        flatten(eVal, lVal || {}, nowFragment)
      }
    })
  })(enTree, langTree, false)

  // Longer fragments matched first so "Bill Amount: " wins over "Bill "
  fragments.sort(function (a, b) { return b.en.length - a.en.length })

  console.log(
    '[translator] built —',
    Object.keys(forwardMap).length, 'exact entries,',
    fragments.length, 'fragments'
  )
}

// ── Text node translation ─────────────────────────────────────────────────────

function translateNode (node) {
  var raw = node.nodeValue
  if (!raw || !raw.trim()) return

  // Skip Material Icons / Vuetify v-icon ligatures — their text IS the icon
  if (isIconElement(node.parentElement)) return

  var trimmed     = raw.trim()
  var replacement = raw

  if (currentLang === 'en') {
    // Restore: Hindi (or whatever) → English
    if (reverseMap[trimmed]) {
      replacement = raw.replace(trimmed, reverseMap[trimmed])
    } else if (fragments.length) {
      // Reverse fragment substitution for mixed content
      var workingR = raw
      var changedR = false
      for (var i = 0; i < fragments.length; i++) {
        var f = fragments[i]
        if (workingR.indexOf(f.lng) !== -1) {
          workingR = workingR.split(f.lng).join(f.en)
          changedR = true
        }
      }
      if (changedR) replacement = workingR
    }
  } else {
    // Translate: English → current language
    if (forwardMap[trimmed]) {
      replacement = raw.replace(trimmed, forwardMap[trimmed])
    } else if (fragments.length) {
      // Forward fragment substitution for mixed content
      var workingF = raw
      var changedF = false
      for (var j = 0; j < fragments.length; j++) {
        var ff = fragments[j]
        if (workingF.indexOf(ff.en) !== -1) {
          workingF = workingF.split(ff.en).join(ff.lng)
          changedF = true
        }
      }
      if (changedF) replacement = workingF
    }
  }

  if (replacement === raw) return

  _paused = true
  node.nodeValue = replacement
  _paused = false

  console.log('[translator]', JSON.stringify(raw.trim()), '→', JSON.stringify(replacement.trim()))
}

// ── DOM walker ────────────────────────────────────────────────────────────────

function walkElement (el) {
  if (!el) return
  el.childNodes.forEach(function (node) {
    if (node.nodeType === 3) {                                          // TEXT_NODE
      translateNode(node)
    } else if (node.nodeType === 1 && !SKIP_TAGS[node.tagName]) {       // ELEMENT_NODE
      if (isIconElement(node)) return                                   // don't descend into icon
      walkElement(node)
    }
  })
}

// ── MutationObserver ──────────────────────────────────────────────────────────

function startObserver () {
  if (!document.body) {
    console.warn('[translator] document.body not ready — observer not started')
    return
  }

  observer = new MutationObserver(function (mutations) {
    if (_paused) return

    mutations.forEach(function (mutation) {
      if (mutation.type === 'characterData') {
        translateNode(mutation.target)
      } else if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 3) {
            translateNode(node)
          } else if (node.nodeType === 1 && !SKIP_TAGS[node.tagName]) {
            if (isIconElement(node)) return                              // skip icon subtree
            walkElement(node)
          }
        })
      }
    })
  })

  observer.observe(document.body, {
    childList:     true,  // new nodes added anywhere in the tree
    subtree:       true,  // watch the entire document
    characterData: true   // Vue reactive value updates
  })

  console.log('[translator] MutationObserver attached to document.body')
}

// ── Language application ──────────────────────────────────────────────────────

async function applyLanguage (lang) {
  console.log('[translator] ── applying language:', lang, '──')

  await loadLocale('en')
  if (lang !== 'en') await loadLocale(lang)

  // If we're currently on a non-English language, restore the DOM back to
  // English FIRST using the existing reverseMap. This must happen before we
  // rebuild the maps for the new language — otherwise the DOM contains text
  // in the old target language (e.g. Hindi) while forwardMap holds en→new
  // (e.g. en→hinglish) entries that don't match, and nothing gets translated.
  if (currentLang !== 'en' && document.body) {
    currentLang = 'en'
    walkElement(document.body)
  }

  if (lang === 'en') {
    buildMaps('en')                                  // clear maps
    currentLang = 'en'
  } else {
    buildMaps(lang)
    currentLang = lang
    if (document.body) walkElement(document.body)
  }
}

// ── Vue Plugin ────────────────────────────────────────────────────────────────

var GlobalTranslator = {
  install: function (Vue) {
    Vue.mixin({
      mounted: function () {
        if (this !== this.$root) return            // run once on root only

        console.log('[translator] root Vue instance mounted — initialising')
        startObserver()

        var savedLang = this.$root.selectedLanguage || 'en'
        if (savedLang !== 'en') {
          applyLanguage(savedLang)
        }

        this.$watch('selectedLanguage', function (newLang) {
          applyLanguage(newLang || 'en')
        })
      }
    })
  }
}

export { forwardMap, applyLanguage }
export default GlobalTranslator
